import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Footer } from '../common';
import HomeNavBar from '../HomePage/HomeNavBar';
import LeftSidebar from '../HomePage/LeftSidebar';
import { ROUTES, TRANSACTION_STATUS_COLORS } from '../../constants';
import {
  authApi,
  clearStoredAuth,
  mergeStoredUser,
  readStoredToken,
  readStoredUser,
} from '../../services/authApi';
import './WalletPage.css';

const SPADE_TO_USD = 0.001996;

function WalletPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const [stripeReturn, setStripeReturn] = useState(
    params.get('purchase') || params.get('connect') || null
  );
  const [stripeSessionId] = useState(() => params.get('session_id') || null);
  const [user, setUser] = useState(() => {
    const stored = readStoredUser() || {};
    return {
      id: stored.id || null,
      name: stored.username || stored.firstName || 'StreamerPro',
      avatar: stored.profileImage || null,
      spades: stored.spadesBalance || 0,
      notifications: 12,
    };
  });
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [earnings, setEarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cashoutAmount, setCashoutAmount] = useState('');
  const [cashoutProcessing, setCashoutProcessing] = useState(false);
  const [cashoutError, setCashoutError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(null);
  const [hasBankAccount, setHasBankAccount] = useState(null);
  const [stripeVerificationError, setStripeVerificationError] = useState(null);

  useEffect(() => {
    if (!stripeReturn) {
      return undefined;
    }

    window.history.replaceState(window.history.state, '', location.pathname);
    const timer = window.setTimeout(() => setStripeReturn(null), 4000);
    return () => window.clearTimeout(timer);
  }, [location.pathname, stripeReturn]);

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const token = readStoredToken();
      const [userResponse, balance, connectStatus, transactions, cashoutHistory] = await Promise.all([
        token ? authApi.getCurrentUser(token).catch(() => null) : Promise.resolve(null),
        authApi.getPaymentBalance().catch(() => null),
        authApi.getConnectStatus().catch(() => ({ has_bank_account: false })),
        authApi.getPaymentLedger().catch(() => []),
        authApi.getCashoutHistory().catch(() => []),
      ]);

      const currentUser = userResponse?.user;
      if (currentUser) {
        mergeStoredUser(currentUser);
      }

      setUser((prev) => ({
        ...prev,
        id: currentUser?.id || prev.id,
        name: currentUser?.username || currentUser?.firstName || prev.name,
        avatar: currentUser?.profileImage || prev.avatar,
        spades: typeof balance?.spade_balance === 'number' ? balance.spade_balance : prev.spades,
      }));
      setHasBankAccount(Boolean(connectStatus?.has_bank_account));

      const cashoutStatusMap = Object.fromEntries(
        Array.isArray(cashoutHistory)
          ? cashoutHistory.map((entry) => [entry.transfer_id, entry.status])
          : []
      );

      const orderRows = Array.isArray(transactions) && transactions.length
        ? transactions
            .filter((tx) => [
              'purchase',
              'spend',
              'cashout',
              'refund',
              'cashout_refund',
              'message_sent',
              'message_refund',
            ].includes(tx.type))
            .map((tx, index) => txnToOrder(tx, index, cashoutStatusMap))
        : [];
      const earningRows = Array.isArray(transactions) && transactions.length
        ? transactions
            .filter((tx) => ['earning', 'message_received'].includes(tx.type))
            .map((tx, index) => txnToEarning(tx, index))
        : [];

      setOrders(orderRows);
      setEarnings(earningRows);
    } catch (error) {
      console.error('Failed to load wallet data:', error);
      setOrders([]);
      setEarnings([]);
      setHasBankAccount(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (stripeReturn !== 'success' || !stripeSessionId) {
      return undefined;
    }

    let cancelled = false;
    setStripeVerificationError(null);

    const verify = async () => {
      try {
        const data = await authApi.verifyStripeSession(stripeSessionId);
        if (!cancelled && typeof data?.spade_balance === 'number') {
          setUser((prev) => ({
            ...prev,
            spades: data.spade_balance,
          }));
        }
        if (!cancelled) {
          fetchData();
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to verify Stripe session:', error);
          setStripeVerificationError(error.message || 'Payment completed, but balance refresh failed.');
        }
      }
    };

    verify();

    return () => {
      cancelled = true;
    };
  }, [fetchData, stripeReturn, stripeSessionId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const handleVisible = () => {
      if (document.visibilityState === 'visible') {
        fetchData();
      }
    };

    document.addEventListener('visibilitychange', handleVisible);
    return () => {
      document.removeEventListener('visibilitychange', handleVisible);
    };
  }, [fetchData]);

  const handleLogout = useCallback(async () => {
    const token = readStoredToken();
    await authApi.logout(token);
    clearStoredAuth();
    navigate(ROUTES.WELCOME);
  }, [navigate]);

  const handlePurchaseComplete = useCallback((result) => {
    const nextBalance = result?.new_balance ?? result?.spade_balance;
    if (typeof nextBalance === 'number') {
      setUser((prev) => ({
        ...prev,
        spades: nextBalance,
      }));
      setStripeReturn('success');
      fetchData();
    }
  }, [fetchData]);

  const handleConnectBank = async () => {
    try {
      setCashoutError(null);
      const data = await authApi.startConnectOnboard();
      window.location.href = data.onboarding_url;
    } catch (error) {
      setCashoutError(error.message || 'Could not start bank onboarding. Try again.');
    }
  };

  const handleCashoutClick = () => {
    const amount = parseInt(cashoutAmount, 10);
    if (!amount || amount <= 0) {
      setCashoutError('Enter a valid amount');
      return;
    }
    if (amount < 2500) {
      setCashoutError('Minimum cashout is 2,500 spades ($4.99)');
      return;
    }
    if (amount > user.spades) {
      setCashoutError('Amount exceeds your balance');
      return;
    }
    setCashoutError(null);
    setShowConfirm(true);
  };

  const handleCashoutConfirm = async () => {
    const amount = parseInt(cashoutAmount, 10);
    setShowConfirm(false);
    setCashoutProcessing(true);

    try {
      const result = await authApi.cashoutSpades(amount);
      setUser((prev) => ({
        ...prev,
        spades: result.new_balance,
      }));
      setCashoutAmount('');
      setShowSuccessModal({ spades: amount, dollars: result.dollars_amount });
      fetchData();
    } catch (error) {
      setCashoutError(error.message || 'Cashout failed');
    } finally {
      setCashoutProcessing(false);
    }
  };

  return (
    <div className="wallet-page">
      <HomeNavBar
        spadesBalance={user.spades}
        notificationCount={user.notifications}
        userAvatar={user.avatar}
        userName={user.name}
        onSpadesClick={() => {}}
        onSpadesPurchaseComplete={handlePurchaseComplete}
        onLogout={handleLogout}
      />

      <LeftSidebar />

      <main className="wallet-page__main">
        <div className="wallet-page__content">
          <h1 className="wallet-page__title">Wallet</h1>

          {stripeReturn === 'success' && (
            <div className="wallet-page__banner wallet-page__banner--success">
              Payment completed. Your spades balance has been refreshed.
            </div>
          )}
          {stripeReturn === 'cancelled' && (
            <div className="wallet-page__banner wallet-page__banner--warn">
              Payment was cancelled. No charges were made.
            </div>
          )}
          {stripeReturn === 'complete' && (
            <div className="wallet-page__banner wallet-page__banner--success">
              Bank account connected. You can now cash out your spades.
            </div>
          )}
          {stripeReturn === 'refresh' && (
            <div className="wallet-page__banner wallet-page__banner--warn">
              The onboarding link expired. Please try connecting your bank again.
            </div>
          )}
          {stripeVerificationError && (
            <div className="wallet-page__banner wallet-page__banner--warn">
              {stripeVerificationError}
            </div>
          )}

          <div className="wallet-page__cashout-card">
            <div className="wallet-page__cashout-balance">
              <span className="wallet-page__cashout-label">Your Balance</span>
              <span className="wallet-page__cashout-amount">
                <span className="wallet-page__spade-icon">&#9824;</span>
                {user.spades.toLocaleString()} Spades
              </span>
            </div>

            {hasBankAccount === false ? (
              <div className="wallet-page__connect-bank">
                <p className="wallet-page__connect-msg">
                  Connect your bank account to cash out your spades.
                </p>
                <button
                  type="button"
                  className="wallet-page__cashout-btn"
                  onClick={handleConnectBank}
                >
                  Connect Bank Account
                </button>
              </div>
            ) : (
              <div className="wallet-page__cashout-form">
                <input
                  type="number"
                  className="wallet-page__cashout-input"
                  placeholder="Amount to cash out (min 2,500)"
                  value={cashoutAmount}
                  onChange={(event) => {
                    setCashoutAmount(event.target.value);
                    setCashoutError(null);
                  }}
                  min="2500"
                  max={user.spades}
                />
                <button
                  type="button"
                  className="wallet-page__cashout-btn"
                  onClick={handleCashoutClick}
                  disabled={cashoutProcessing || !cashoutAmount}
                >
                  {cashoutProcessing ? 'Processing...' : 'Cash Out'}
                </button>
              </div>
            )}

            {cashoutError && (
              <p className="wallet-page__cashout-error">{cashoutError}</p>
            )}
          </div>

          {showConfirm && (
            <div className="wallet-page__modal-overlay">
              <div className="wallet-page__modal">
                <h3 className="wallet-page__modal-title">Confirm Cashout</h3>
                <p className="wallet-page__modal-body">
                  You are about to cash out{' '}
                  <strong>&#9824; {parseInt(cashoutAmount, 10).toLocaleString()} Spades</strong>
                  {' '}for{' '}
                  <strong>${(parseInt(cashoutAmount, 10) * SPADE_TO_USD).toFixed(2)}</strong>.
                </p>
                <p className="wallet-page__modal-note">
                  The amount will be processed and transferred to your account.
                </p>
                <div className="wallet-page__modal-actions">
                  <button
                    type="button"
                    className="wallet-page__modal-btn wallet-page__modal-btn--cancel"
                    onClick={() => setShowConfirm(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="wallet-page__modal-btn wallet-page__modal-btn--confirm"
                    onClick={handleCashoutConfirm}
                  >
                    Confirm Cashout
                  </button>
                </div>
              </div>
            </div>
          )}

          {showSuccessModal && (
            <div className="wallet-page__modal-overlay">
              <div className="wallet-page__modal">
                <div className="wallet-page__modal-success-icon">&#10003;</div>
                <h3 className="wallet-page__modal-title">Cashout Successful</h3>
                <p className="wallet-page__modal-body">
                  <strong>&#9824; {showSuccessModal.spades.toLocaleString()} Spades</strong>
                  {' '}have been cashed out for{' '}
                  <strong className="wallet-page__modal-amount">
                    ${showSuccessModal.dollars.toFixed(2)}
                  </strong>.
                </p>
                <p className="wallet-page__modal-note">
                  Your payout is being processed. Funds will transfer shortly.
                </p>
                <button
                  type="button"
                  className="wallet-page__modal-btn wallet-page__modal-btn--confirm"
                  onClick={() => setShowSuccessModal(null)}
                >
                  Done
                </button>
              </div>
            </div>
          )}

          <div className="wallet-page__tabs">
            <button
              type="button"
              className={`wallet-page__tab ${activeTab === 'orders' ? 'wallet-page__tab--active' : ''}`}
              onClick={() => setActiveTab('orders')}
            >
              Past Orders
            </button>
            <button
              type="button"
              className={`wallet-page__tab ${activeTab === 'earnings' ? 'wallet-page__tab--active' : ''}`}
              onClick={() => setActiveTab('earnings')}
            >
              Earnings
            </button>
          </div>

          <div className="wallet-page__table-container">
            {loading ? (
              <p className="wallet-page__loading">Loading transactions...</p>
            ) : activeTab === 'orders' ? (
              <OrdersTable orders={orders} />
            ) : (
              <EarningsTable earnings={earnings} />
            )}
          </div>
        </div>

        <Footer />
      </main>
    </div>
  );
}

function getContentBadgeClass(content) {
  return `wallet-page__content-badge wallet-page__content-badge--${content
    .toLowerCase()
    .replace(/\s+/g, '-')}`;
}

function txnToOrder(tx, index, cashoutStatusMap = {}) {
  const date = tx.created_at ? new Date(tx.created_at).toLocaleDateString() : '';
  const spades = Math.abs(Number(tx.amount)) || 0;
  const isCashout = tx.type === 'cashout' || tx.type === 'cashout_refund';
  const isMessage = tx.type === 'message_sent';
  const isMessageRefund = tx.type === 'message_refund';
  const dollars = tx.type === 'purchase'
    ? (spades / 2500) * 4.99
    : spades * SPADE_TO_USD;
  const messageAccount = tx.streamer_username || tx.creator_username || 'Direct Message';
  const account = isMessage || isMessageRefund
    ? messageAccount
    : tx.type === 'spend'
      ? 'Streamer'
      : isCashout
        ? 'Bank Payout'
        : 'Polar Ace';
  const content = tx.type === 'purchase'
    ? 'Purchase'
    : isMessage
      ? 'Message'
      : tx.type === 'spend'
        ? 'Stream'
        : tx.type === 'refund' || isMessageRefund
          ? 'Refund'
          : 'Cash Out';

  let status = 'Completed';
  if (isCashout) {
    const realStatus = cashoutStatusMap[tx.ref_id];
    status = realStatus === 'completed' ? 'Completed' : 'Processing';
  }
  if (isMessageRefund) {
    status = 'Refunded';
  }

  return {
    id: tx.ref_id || `order-${index}`,
    account,
    content,
    date,
    chips: spades,
    dollars,
    status,
  };
}

function parseEarningDescription(description) {
  if (!description || !description.startsWith('from:')) {
    return { fan: 'Unknown', mediaType: 'Image', duration: 1 };
  }

  const parts = Object.fromEntries(description.split('|').map((part) => part.split(':')));
  return {
    fan: parts.from || 'Unknown',
    mediaType: parts.type === 'gif' ? 'GIF' : 'Image',
    duration: parseInt(parts.duration, 10) || 1,
  };
}

function txnToEarning(tx, index) {
  const date = tx.created_at ? new Date(tx.created_at).toLocaleDateString() : '';
  const spades = Math.abs(Number(tx.amount)) || 0;
  if (tx.type === 'message_received') {
    return {
      id: tx.ref_id || `earning-${index}`,
      fan: tx.fan_username || 'Unknown',
      mediaType: 'Message',
      duration: null,
      durationLabel: 'DM',
      date,
      spades,
      dollars: spades * SPADE_TO_USD,
      status: 'Received',
    };
  }
  const parsed = parseEarningDescription(tx.description);
  const mediaType = tx.media_type
    ? tx.media_type.toLowerCase() === 'gif'
      ? 'GIF'
      : 'Image'
    : parsed.mediaType;
  const duration = typeof tx.duration_minutes === 'number' && tx.duration_minutes > 0
    ? tx.duration_minutes
    : parsed.duration;
  const fan = tx.fan_username || parsed.fan;

  return {
    id: tx.ref_id || `earning-${index}`,
    fan,
    mediaType,
    duration,
    durationLabel: `${duration} min`,
    date,
    spades,
    dollars: spades * SPADE_TO_USD,
    status: 'Received',
  };
}

function OrdersTable({ orders }) {
  if (!orders.length) {
    return <p className="wallet-page__empty">No orders yet.</p>;
  }

  return (
    <table className="wallet-page__table">
      <thead>
        <tr>
          <th>Account</th>
          <th>Content</th>
          <th>Date</th>
          <th>Spades</th>
          <th>Amount</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {orders.map((order) => (
          <tr key={order.id}>
            <td>{order.account}</td>
            <td>
              <span className={getContentBadgeClass(order.content)}>
                {order.content}
              </span>
            </td>
            <td>{order.date}</td>
            <td>
              <span className="wallet-page__amount">
                <span className="wallet-page__spade-icon">&#9824;</span>
                {order.chips.toLocaleString()}
              </span>
            </td>
            <td>
              <span className="wallet-page__amount">
                ${order.dollars.toFixed(2)}
              </span>
            </td>
            <td>
              <span
                className="wallet-page__status"
                style={{ color: TRANSACTION_STATUS_COLORS[order.status] || 'var(--text-body)' }}
              >
                {order.status}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function EarningsTable({ earnings }) {
  if (!earnings.length) {
    return <p className="wallet-page__empty">No earnings yet.</p>;
  }

  return (
    <table className="wallet-page__table">
      <thead>
        <tr>
          <th>Account</th>
          <th>Type</th>
          <th>Duration</th>
          <th>Date</th>
          <th>Spades</th>
          <th>Amount</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {earnings.map((earning) => (
          <tr key={earning.id}>
            <td>{earning.fan}</td>
            <td>
              <span className={getContentBadgeClass(earning.mediaType)}>
                {earning.mediaType}
              </span>
            </td>
            <td>{earning.durationLabel || (earning.duration != null ? `${earning.duration} min` : '—')}</td>
            <td>{earning.date}</td>
            <td>
              <span className="wallet-page__amount">
                <span className="wallet-page__spade-icon">&#9824;</span>
                {earning.spades.toLocaleString()}
              </span>
            </td>
            <td>
              <span className="wallet-page__amount">
                ${earning.dollars.toFixed(2)}
              </span>
            </td>
            <td>
              <span
                className="wallet-page__status"
                style={{ color: TRANSACTION_STATUS_COLORS[earning.status] || 'var(--text-body)' }}
              >
                {earning.status}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default WalletPage;
